import { memo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, Group, Text, Button } from 'components'
import { ScrollArea } from 'hooks'
import { SkillsStandardsContext } from '../../../context/SkillsStandardsContext'
import { ItemCard } from './ItemCard'

interface ProjectItem {
  id: string
  name: string
  original_codes?: string
  originalCodes?: string
  cycles?: Array<{ name: string }>
  breadcrumb?: string
  lv1_name?: string
  lv1Name?: string
  color?: string
}

interface TabContentProps {
  items: ProjectItem[]
  type: 'content' | 'skill'
  selectedIds: Set<string>
  onToggleItem: (itemId: string) => void
  onSelectAll: () => void
}

export const TabContent = memo(function TabContent({ 
  items, 
  type, 
  selectedIds, 
  onToggleItem, 
  onSelectAll 
}: TabContentProps) {
  const { t } = useTranslation()
  const { checkIfSelected } = useContext(SkillsStandardsContext)
  
  // Proteger contra items undefined/null
  const validItems = Array.isArray(items) ? items.filter(item => item && item.id) : []
  
  // Verificar quantos itens já foram aplicados
  const alreadyAddedItems = validItems.filter(item => checkIfSelected(item.id, type))
  const availableItems = validItems.filter(item => !checkIfSelected(item.id, type))
  
  // Se todos os itens já foram aplicados, não mostrar controles de seleção
  const allItemsAlreadyAdded = validItems.length > 0 && alreadyAddedItems.length === validItems.length
  
  const allSelected = availableItems.length > 0 && availableItems.every(item => selectedIds.has(item.id))
  const selectedCount = availableItems.filter(item => selectedIds.has(item.id)).length
  
  return (
    <Stack>
      {validItems.length > 0 && !allItemsAlreadyAdded && (
        <Group position='apart'>
          <Text size='sm' color='dimmed'>
            {selectedCount} de {availableItems.length} selecionados
          </Text>
          <Button 
            size='xs' 
            variant='subtle'
            onClick={onSelectAll}
            disabled={availableItems.length === 0}
          >
            {allSelected ? t('new_project_page.unselect_all') : t('new_project_page.select_all')}
          </Button>
        </Group>
      )}
      
      {allItemsAlreadyAdded && (
        <Group position='apart'>
          <Text size='sm' color='dimmed'>
            Todos os {validItems.length} itens já foram adicionados ao projeto
          </Text>
        </Group>
      )}
      
      <ScrollArea style={{ height: 400 }}>
        <Stack spacing='xs'>
          {validItems.map(item => {
            // Proteção adicional para evitar tela branca
            if (!item || !item.id) return null
            
            try {
              return (
                <ItemCard
                  key={item.id}
                  item={item}
                  type={type}
                  isSelected={selectedIds.has(item.id)}
                  isAlreadyAdded={!!checkIfSelected(item.id, type)}
                  onToggle={onToggleItem}
                />
              )
            } catch (error) {
              // Se houver erro renderizando um item, simplesmente ignora
              return null
            }
          }).filter(Boolean)}
        </Stack>
      </ScrollArea>
      
      {!Array.isArray(items) && (
        <Text size='sm' color='dimmed' ta='center' py='lg'>
          Erro: Items não é um array válido
        </Text>
      )}
      
      {Array.isArray(items) && items.length > 0 && validItems.length === 0 && (
        <Text size='sm' color='dimmed' ta='center' py='lg'>
          Nenhum item válido encontrado
        </Text>
      )}
      
      {Array.isArray(items) && items.length === 0 && (
        <Text size='sm' color='dimmed' ta='center' py='lg'>
          Nenhum item disponível
        </Text>
      )}
    </Stack>
  )
})